import {
  BookOpen,
  FileText,
  FileType,
  Headphones,
  HelpCircle,
  Mail,
  Sparkles,
  MessageCircle,
  Video,
  type LucideIcon,
} from "lucide-react";

export type SourceTokenKey =
  | "book"
  | "article"
  | "supplemental"
  | "podcast"
  | "tweet"
  | "pdf"
  | "email"
  | "video"
  | "unknown";

export const SOURCE_MAP: Record<
  string,
  { label: string; Icon: LucideIcon; tokenKey: SourceTokenKey }
> = {
  books: { label: "Book", Icon: BookOpen, tokenKey: "book" },
  book: { label: "Book", Icon: BookOpen, tokenKey: "book" },
  articles: { label: "Article", Icon: FileText, tokenKey: "article" },
  article: { label: "Article", Icon: FileText, tokenKey: "article" },
  supplemental: { label: "Supplemental", Icon: Sparkles, tokenKey: "supplemental" },
  podcasts: { label: "Podcast", Icon: Headphones, tokenKey: "podcast" },
  podcast: { label: "Podcast", Icon: Headphones, tokenKey: "podcast" },
  tweets: { label: "Tweet", Icon: MessageCircle, tokenKey: "tweet" },
  twitter: { label: "Tweet", Icon: MessageCircle, tokenKey: "tweet" },
  pdf: { label: "PDF", Icon: FileType, tokenKey: "pdf" },
  pdfs: { label: "PDF", Icon: FileType, tokenKey: "pdf" },
  email: { label: "Email", Icon: Mail, tokenKey: "email" },
  emails: { label: "Email", Icon: Mail, tokenKey: "email" },
  youtube: { label: "Video", Icon: Video, tokenKey: "video" },
  video: { label: "Video", Icon: Video, tokenKey: "video" },
  videos: { label: "Video", Icon: Video, tokenKey: "video" },
};

const titleCase = (s: string) =>
  s.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export const getSourceDisplay = (raw: string | null) => {
  if (!raw)
    return {
      label: "Unknown",
      Icon: HelpCircle,
      key: "__unknown__",
      tokenKey: "unknown" as SourceTokenKey,
    };
  const key = raw.toLowerCase().trim();
  const mapped = SOURCE_MAP[key];
  if (mapped) return { ...mapped, key };
  return {
    label: titleCase(raw),
    Icon: HelpCircle,
    key,
    tokenKey: "unknown" as SourceTokenKey,
  };
};

export const getSourceChipStyle = (tokenKey: SourceTokenKey) => ({
  background: `hsl(var(--chip-source-${tokenKey}-bg))`,
  color: `hsl(var(--chip-source-${tokenKey}-fg))`,
  borderColor: `hsl(var(--chip-source-${tokenKey}-border))`,
});

export const getFavoriteChipStyle = () => ({
  background: `hsl(var(--chip-favorite-bg))`,
  color: `hsl(var(--chip-favorite-fg))`,
  borderColor: `hsl(var(--chip-favorite-border))`,
});

export const FAVORITE_LABEL = "Favorite";
export const isFavoriteTag = (t: string) => /^favorite$/i.test(t);

export type ReadwiseHighlightRow = {
  id: string;
  external_id: string;
  book_title: string | null;
  book_author: string | null;
  book_source: string | null;
  book_cover_url: string | null;
  highlight_text: string;
  note: string | null;
  location: string | null;
  location_type: string | null;
  highlighted_at: string | null;
  readwise_url: string | null;
  readwise_tags: string[];
};

export type ReadwiseLibraryMatch = {
  id: string;
  title: string;
  subtitle: string | null;
  display_title: string | null;
  author_text: string | null;
};

export const buildReadwiseReturnTo = (params?: { q?: string; book?: string; tag?: string; source?: string }) => {
  const next = new URLSearchParams();
  if (params?.q?.trim()) next.set("q", params.q.trim());
  if (params?.book && params.book !== "__all__") next.set("book", params.book);
  if (params?.tag && params.tag !== "__all__") next.set("tag", params.tag);
  if (params?.source && params.source !== "__all__") next.set("source", params.source);
  const query = next.toString();
  return `readwise-highlights${query ? `?${query}` : ""}`;
};
